import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Plus, Eye, Send, Trash2, ExternalLink, FileText, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WhatsAppTemplatesPanel = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isConfigured, setIsConfigured] = useState(true);
  const [viewTpl, setViewTpl] = useState(null);
  const [testTpl, setTestTpl] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/whatsapp/templates`, { headers });
      setTemplates(r.data.templates || []);
      setIsConfigured(true);
    } catch (e) {
      if (e?.response?.status === 400 || e?.response?.data?.detail?.toLowerCase?.().includes('not configured')) {
        setIsConfigured(false);
      } else {
        toast.error('Failed to load WhatsApp templates');
      }
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusBadge = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'APPROVED') return <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
    if (s === 'REJECTED') return <Badge className="bg-red-500/20 text-red-300 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
    if (s === 'PENDING') return <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    return <Badge variant="outline" className="text-gray-400">{status || 'Unknown'}</Badge>;
  };

  const deleteTpl = async (name) => {
    if (!window.confirm(`Delete WhatsApp template "${name}"? This cannot be undone in Meta.`)) return;
    try {
      await axios.delete(`${API}/whatsapp/templates/${encodeURIComponent(name)}`, { headers });
      toast.success('Template deleted');
      load();
    } catch (e) {
      toast.error('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const sendTest = async () => {
    if (!testPhone || !testTpl) return;
    setSending(true);
    try {
      await axios.post(`${API}/whatsapp/send-template`, {
        to: testPhone,
        template_name: testTpl.name,
        language: testTpl.language || 'en',
        variables: [],
      }, { headers });
      toast.success(`Test sent to ${testPhone}`);
      setTestTpl(null);
      setTestPhone('');
    } catch (e) {
      toast.error('Send failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setSending(false); }
  };

  if (!isConfigured) {
    return (
      <div className="p-10 text-center border border-dashed border-wt-border rounded-lg">
        <FileText className="w-10 h-10 text-gray-500 mx-auto mb-3" />
        <div className="text-white font-medium">WhatsApp not connected</div>
        <div className="text-sm text-gray-500 mt-1 mb-4">Connect your WhatsApp Business account to manage templates.</div>
        <Button onClick={() => navigate('/integrations/whatsapp')} variant="outline">
          <ExternalLink className="w-4 h-4 mr-1" /> Go to WhatsApp Integration
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="wa-templates-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-gray-400">
          Managed via Meta Cloud API · Templates must be approved by Meta before sending.
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="wa-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => navigate('/integrations/whatsapp?tab=templates&action=create')} className="bg-green-500 hover:bg-green-600 text-white" data-testid="wa-create-tpl">
            <Plus className="w-4 h-4 mr-1" /> Create Template
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
      ) : templates.length === 0 ? (
        <div className="p-10 text-center border border-dashed border-wt-border rounded-lg">
          <FileText className="w-10 h-10 text-gray-500 mx-auto mb-3" />
          <div className="text-white font-medium">No WhatsApp templates yet</div>
          <div className="text-sm text-gray-500 mt-1 mb-4">Create your first template to start sending broadcasts.</div>
          <Button onClick={() => navigate('/integrations/whatsapp?tab=templates&action=create')} className="bg-green-500 hover:bg-green-600 text-white">
            <Plus className="w-4 h-4 mr-1" /> Create Template
          </Button>
        </div>
      ) : (
        <div className="bg-wt-surface border border-wt-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-wt-border">
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Language</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((t) => (
                <TableRow key={t.id || t.name} className="border-wt-border">
                  <TableCell className="font-medium text-white">{t.name}</TableCell>
                  <TableCell className="text-gray-400">{t.category}</TableCell>
                  <TableCell className="text-gray-400">{t.language}</TableCell>
                  <TableCell>{statusBadge(t.status)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setViewTpl(t)} title="View"><Eye className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setTestTpl(t)} disabled={t.status !== 'APPROVED'} title="Send Test"><Send className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteTpl(t.name)} title="Delete"><Trash2 className="w-4 h-4 text-red-400" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!viewTpl} onOpenChange={(v) => !v && setViewTpl(null)}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-2xl">
          <DialogHeader><DialogTitle className="text-white">{viewTpl?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">{viewTpl?.category}</Badge>
              <Badge variant="outline">{viewTpl?.language}</Badge>
              {statusBadge(viewTpl?.status)}
            </div>
            {(viewTpl?.components || []).map((c, i) => (
              <div key={i} className="p-3 bg-wt-bg border border-wt-border rounded">
                <div className="text-xs uppercase text-gray-500 mb-1">{c.type}</div>
                <div className="text-gray-200 whitespace-pre-wrap break-words">{c.text || c.format || JSON.stringify(c.buttons || {})}</div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setViewTpl(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!testTpl} onOpenChange={(v) => !v && setTestTpl(null)}>
        <DialogContent className="bg-wt-surface border-wt-border">
          <DialogHeader><DialogTitle className="text-white">Send Test — {testTpl?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white">Recipient phone (with country code)</Label>
              <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+971501234567" className="bg-wt-bg mt-1" data-testid="wa-test-phone" />
            </div>
            <div className="text-xs text-gray-500">
              Note: If this template has variables, send from the WhatsApp Integrations page for full variable support.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTestTpl(null)}>Cancel</Button>
            <Button onClick={sendTest} disabled={!testPhone || sending} data-testid="wa-submit-test">{sending ? 'Sending…' : 'Send'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppTemplatesPanel;
